import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Button, Heading } from '@frontend/design-system';
import { InsuranceReportWritebackStatusMessageContainer } from '../../../components/insurance-report-writeback-status-message';
import { useDentalInsuranceReportContext } from '../../../dental-insurance-eligibility-report/dental-insurance-eligibility-report/context/DentalInsuranceReportDetailsContext.provider';
import { useInsuranceEligibilityReportContext } from '../../../dental-insurance-eligibility-report/onederful-insurance-eligibility-report/context/OnederfulReportContext.provider';
import { useInsuranceEligibilityReportContainerContext } from '../../../providers/insurance-report-container.provider';

type InsuranceVerificationReportHeaderProps = {
  handleSaveToPms?: () => void;
  handlePrint?: () => void;
  closeInsuranceReportModal: () => void;
  PMSName: string;
};
export const InsuranceVerificationReportHeader = ({
  PMSName,
  handlePrint,
  handleSaveToPms,
  closeInsuranceReportModal,
}: InsuranceVerificationReportHeaderProps) => {
  const { profileData } = useInsuranceEligibilityReportContext();
  const { t } = useTranslation('insuranceEligibilityReport');

  const {
    insuranceEligibilityReportData: { dentalSummaryInfo: { patient } = {} },
  } = useDentalInsuranceReportContext();

  const { integrationDetails } = useInsuranceEligibilityReportContainerContext();
  const shouldShowSaveToPmsButton = integrationDetails?.isDocumentCenterWritebackSupported && !!PMSName;

  const reportPersonName = useMemo<string>(() => {
    const firstName = profileData?.firstName || patient?.firstName;
    if (firstName) {
      return `${titleCase(firstName)}'s`;
    }
    return '';
  }, [profileData, patient]);

  if (!reportPersonName) return null;

  const translationReportPersonName = t('{{reportPersonName}} Insurance Verification Report', {
    reportPersonName,
  });
  return (
    <header css={reportHeaderStyles}>
      <div css={headerContentStyles}>
        <div css={headerDetailsStyle}>
          <Heading
            level={1}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '650px', whiteSpace: 'nowrap' }}
          >
            {translationReportPersonName.trim()}
          </Heading>
        </div>
        <div css={buttonContainerStyle}>
          <InsuranceReportWritebackStatusMessageContainer />
          <Button variant='primary' size='small' onClick={handlePrint} iconName='download-small' />
          {shouldShowSaveToPmsButton && (
            <Button
              variant='secondary'
              size='small'
              onClick={handleSaveToPms}
              trackingId='save-to-pms-button-tuuthfairy-report'
              css={{ width: 'fit-content' }}
            >
              {t('Save to {{PMSName}}', { PMSName })}
            </Button>
          )}
          <Button variant='secondary' size='small' onClick={closeInsuranceReportModal} iconName='x' />
        </div>
      </div>
    </header>
  );
};

const reportHeaderStyles = css({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  zIndex: theme.zIndex.high,
});

const headerContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 2),
  height: theme.spacing(9),
  backgroundColor: theme.colors.white,
  zIndex: theme.zIndex.high,
  boxShadow: theme.shadows.light,
  width: '100%',
});

const buttonContainerStyle = css`
  background-color: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing(1)};
  @media print {
    display: none;
  }
`;

const headerDetailsStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex-wrap: wrap;
  margin-right: ${theme.spacing(1)};
  h1 {
    margin-right: ${theme.spacing(1)};
  }
`;
